import { useRecruitmentApi } from 'infrastructure/api';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResourceData, ResourceId } from 'framework/crud';
import { useCandidateUrl } from 'domain/shared/candidate';
import { SCAN_READ_NOT_ALLOWED_REGEXP } from './constants';
import { MainCandidate } from './types';

const useDuplicateScanService = () => {
  const [mainCandidate, setMainCandidate] = useState<MainCandidate>({});
  const { sendGetRequest, sendPutRequest } = useRecruitmentApi();
  const { getSingleEntityUrl } = useCandidateUrl();
  const navigate = useNavigate();

  const getById = async (id: ResourceId) => {
    try {
      const response = await sendGetRequest(`/candidates/duplicate-scan/${id}`);
      const data = await response.json();
      setMainCandidate(data.mainCandidate);
      return {
        ...data,
        actualDuplicateIds: [],
      };
    } catch (error) {
      if (!(error instanceof Response)) {
        throw error;
      }

      const errorData = await error.json().catch(() => null);

      const blockedStatus = errorData?.message?.match(SCAN_READ_NOT_ALLOWED_REGEXP)?.[1];

      if (error.status !== 400 || !blockedStatus) {
        throw error;
      }

      return {
        scanReadBlockedStatus: blockedStatus,
        mainCandidate: null,
        possibleDuplicates: [],
        actualDuplicateIds: [],
      };
    }
  };

  const process = async (id: ResourceId, data: ResourceData) => {
    const response = await sendPutRequest(`/candidates/duplicate-scan/${id}/process`, {
      actualDuplicateIds: data.actualDuplicateIds,
    });
    return response.json();
  };

  const redirectCallback = useCallback(() => {
    navigate(getSingleEntityUrl(mainCandidate.id));
  }, [getSingleEntityUrl, mainCandidate.id, navigate]);

  return {
    redirectCallback,
    mainCandidate,
    getById,
    process,
  };
};

export default useDuplicateScanService;
